'use client';

import { ColumnDef } from '@tanstack/react-table';
import { Ellipsis } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ResourceBadge } from '@/components/ui/resource-badge';
import { UpsellItem } from '@/lib/api/upsells';
import { DateDisplay } from '@/components/date-display';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { UpsellResourceBadge } from '@/components/upsell/UpsellResourceBadge';

interface GetUpsellsTableColumnsProps {
    storeId: string;
    onEditUpsell: (upsellId: string) => void;
    onDeleteUpsell: (upsellId: string) => void;
}

export const useUpsellsTableColumns = ({
    storeId,
    onEditUpsell,
    onDeleteUpsell,
}: GetUpsellsTableColumnsProps): ColumnDef<UpsellItem>[] => {
    const { t } = useTranslation('upsells');

    return React.useMemo(
        () => [
            {
                id: 'select',
                header: ({ table }) => (
                    <Checkbox
                        checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
                        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                        aria-label={t('table.selectAll')}
                        className="translate-y-0.5"
                    />
                ),
                cell: ({ row }) => (
                    <DataTableCheckbox row={row} />
                ),
                enableSorting: false,
                enableHiding: false,
                size: 40,
            },
            {
                id: 'id',
                accessorKey: 'id',
                header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.id')} />,
                cell: ({ row }) => {
                    const upsell = row.original;
                    return (
                        <UpsellResourceBadge
                            upsellId={upsell.id}
                            variant="secondary"
                        />
                    );
                },
                meta: { label: t('table.columns.id') },
                enableSorting: true,
            },
            {
                id: 'title',
                accessorKey: 'offerTitle',
                header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.title')} />,
                cell: ({ row }) => {
                    const upsell = row.original;
                    const { storeRoutes } = useStoreNavigate();
                    return (
                        <div className="min-w-0">
                            <Link to={storeRoutes.UPSELL_EDIT(upsell.id)}>
                                <div className="truncate font-medium hover:underline">{upsell.offerTitle}</div>
                            </Link>
                        </div>
                    );
                },
                enableColumnFilter: true,
                enableSorting: true,
                meta: { label: t('table.columns.title') },
            },
            {
                id: 'type',
                accessorKey: 'type',
                header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.type')} />,
                cell: ({ row }) => {
                    const upsell = row.original;
                    const typeLabels: Record<string, string> = {
                        orderbump: t('table.types.orderbump'),
                        upsell: t('table.types.upsell'),
                    };
                    const typeColors: Record<string, 'default' | 'secondary'> = {
                        orderbump: 'default',
                        upsell: 'secondary',
                    };
                    return (
                        <Badge variant={typeColors[upsell.type] || 'default'}>
                            {typeLabels[upsell.type] || upsell.type}
                        </Badge>
                    );
                },
                enableSorting: true,
                meta: { label: t('table.columns.type') },
            },
            {
                id: 'enabled',
                accessorKey: 'enabled',
                header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.status')} />,
                cell: ({ row }) => {
                    const upsell = row.original;
                    return (
                        <Badge variant={upsell.enabled ? 'default' : 'outline'}>
                            {upsell.enabled ? t('table.status.enabled') : t('table.status.disabled')}
                        </Badge>
                    );
                },
                enableSorting: true,
                meta: { label: t('table.columns.status') },
            },
            {
                id: 'triggers',
                header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.triggers')} />,
                cell: ({ row }) => {
                    const upsell = row.original;
                    const triggerCount = upsell.triggers?.length || 0;
                    return (
                        <div className="text-sm">
                            <span className="font-medium">{triggerCount}</span>
                            <span className="text-muted-foreground ml-1">
                                {triggerCount === 1 ? t('table.trigger') : t('table.triggers')}
                            </span>
                        </div>
                    );
                },
                enableSorting: false,
                meta: { label: t('table.columns.triggers') },
            },
            {
                id: 'offers',
                header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.offers')} />,
                cell: ({ row }) => {
                    const upsell = row.original;
                    const offerCount = upsell.type === 'orderbump'
                        ? (upsell.orderBumpOffers?.length || 0)
                        : (upsell.offers?.length || 0);
                    return (
                        <div className="text-sm">
                            <span className="font-medium">{offerCount}</span>
                            <span className="text-muted-foreground ml-1">
                                {offerCount === 1 ? t('table.offer') : t('table.offers')}
                            </span>
                        </div>
                    );
                },
                enableSorting: false,
                meta: { label: t('table.columns.offers') },
            },
            {
                id: 'createdAt',
                accessorKey: 'createdAt',
                header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.created')} />,
                cell: ({ row }) => (
                    <DateDisplay
                        value={row.original.createdAt}
                        format="MMM dd, yyyy"
                        className="text-sm text-muted-foreground"
                        fallback={t('table.na')}
                    />
                ),
                enableSorting: true,
                meta: { label: t('table.columns.created') },
            },
            {
                id: 'actions',
                cell: ({ row }) => {
                    const upsell = row.original;
                    const { storeRoutes } = useStoreNavigate();
                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    aria-label={t('table.openMenu')}
                                    variant="ghost"
                                    className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
                                >
                                    <Ellipsis className="h-4 w-4" aria-hidden="true" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-[160px]">
                                <DropdownMenuItem onClick={() => onEditUpsell(upsell.id)}>
                                    {t('actions.edit')}
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                    <Link to={storeRoutes.UPSELL_EDIT(upsell.id)}>{t('actions.viewDetails')}</Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => onDeleteUpsell(upsell.id)}
                                    className="text-destructive focus:text-destructive"
                                >
                                    {t('actions.delete')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    );
                },
                enableSorting: false,
                enableHiding: false,
                size: 40,
            },
        ],
        [t, onEditUpsell, onDeleteUpsell]
    );
};
